import moment from 'moment-timezone';
import { AddressInputType } from '@domain/addresses/types/types';
import { ApolloError } from '@apollo/client';
import {
  buildDayToHoursMap,
  buildOrderedDays,
  BusinessHours,
  findNextOpenSlot,
  isRestaurantOpen,
  transformOpenHoursDetails,
} from '@/utils/business-hours';
import { ChatStatus } from '@/types';
import { formatAddressForInput } from '@/utils/address';
import { useGeneratedSalesPortalAccountDetailsQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-account-details.generated';

export interface AccountDetailRestaurant {
  name: string;
  uuid: string;
}

interface AccountDetailsLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

export interface AccountDetailBusinessHours {
  dayToHoursMap: Record<number, BusinessHours[]>;
  isCurrentlyOpen: boolean;
  nextOpenSlot: BusinessHours | null;
  orderedDays: number[];
}

interface AccountDetailsLoaderResult {
  accountId: string | null;
  address: AddressInputType | null;
  businessHours: AccountDetailBusinessHours;
  chatUUID: string | null;
  error: ApolloError | undefined;
  isProspect: boolean;
  loading: boolean;
  notes: string | null;
  refetch: () => Promise<unknown>;
  restaurant: AccountDetailRestaurant | undefined;
  showARPanel: boolean;
}
const EMPTY_BUSINESS_HOURS_ARRAY: BusinessHours[] = [];
/**
 * Loader hook for fetching the restaurant data for the Account Detail screen.
 * Fetches address, account ID, and business hours in a single query.
 * Pre-computes presentation values (open/closed state, day-of-week schedule
 * starting from today) using the restaurant's timezone so results are correct
 * regardless of DSR timezone.
 *
 * @param params.restaurantUUID - The UUID of the restaurant to fetch data for
 * @param params.supplierUUID - The UUID of the supplier
 * @returns The restaurant, formatted address, account ID, hours, loading state, and any error
 */
export const useAccountDetailsLoader = ({
  restaurantUUID,
  supplierUUID,
}: AccountDetailsLoaderParams): AccountDetailsLoaderResult => {
  const { data, loading, error, refetch } =
    useGeneratedSalesPortalAccountDetailsQuery({
      fetchPolicy: 'cache-first',
      variables: {
        restaurantUUID,
        supplierUUID,
      },
    });

  const queryRestaurant = data?.restaurant;
  const restaurant = queryRestaurant
    ? { name: queryRestaurant.name, uuid: queryRestaurant.uuid }
    : undefined;
  const queryAddress = queryRestaurant?.address;
  const timezone = queryAddress?.timezone;

  const restaurantChat = queryRestaurant?.restaurant_chats[0];
  const accountId = restaurantChat?.restaurant_account_id ?? null;
  const chatUUID = restaurantChat?.uuid ?? null;
  const notes = restaurantChat?.restaurant_notes ?? null;
  const showARPanel = restaurantChat?.show_app_payments_tab ?? false;
  const isProspect = restaurantChat?.status === ChatStatus.Prospect;

  const openHours = data?.businessDetails?.hours?.[0];
  const hours = openHours
    ? openHours.open.map(transformOpenHoursDetails)
    : EMPTY_BUSINESS_HOURS_ARRAY;

  const now = timezone ? moment.tz(timezone) : moment();
  const currentDayOfWeek = now.day();
  const currentHour = now.hour();
  const currentMinute = now.minute();

  const dayToHoursMap = buildDayToHoursMap(hours);
  const orderedDays = buildOrderedDays(currentDayOfWeek);
  const nextOpenSlot = findNextOpenSlot(
    dayToHoursMap,
    currentDayOfWeek,
    currentHour,
    currentMinute,
  );
  const isCurrentlyOpen =
    nextOpenSlot !== null &&
    isRestaurantOpen(
      nextOpenSlot,
      currentDayOfWeek,
      currentHour,
      currentMinute,
    );

  return {
    accountId,
    address: queryAddress ? formatAddressForInput(queryAddress) : null,
    businessHours: {
      dayToHoursMap,
      isCurrentlyOpen,
      nextOpenSlot,
      orderedDays,
    },
    chatUUID,
    error,
    isProspect,
    loading,
    notes,
    refetch,
    restaurant,
    showARPanel,
  };
};
